import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Row,
} from 'reactstrap';
import Swal from 'sweetalert2';

import PageHeader from '../../../components/Headers/PageHeader.jsx';
import { AdminStateContext } from '../../../providers/AdminStateProvider.jsx';
import { createBankAccount, getBanks } from '../../../api';
import { useAuthToken } from '../../../hooks/AuthHook.jsx';
import { swalErrorMessage } from '../utils';
import {
  ACCOUNT_TYPES,
  DOCUMENT_LABEL,
  DOCUMENT_PLACEHOLDER,
  DOCUMENT_TYPES,
  buildBankAccountPayload,
  emptyBankAccountForm,
  escapeHtml,
  hasCity,
  hasClabe,
  hasDocumentType,
  parseBanks,
  validateBankAccountForm,
} from './bankAccountForm';

// handleApiResponse already turns the service envelope into a sentence an
// operator can act on; anything reaching here without one is a network failure.
const describe = (error) => error?.message || 'No se pudo completar la solicitud.';

const NewAccount = () => {
  const [adminState] = useContext(AdminStateContext);
  const authToken = useAuthToken();
  const country = (adminState?.country ?? '').toUpperCase();

  const [form, setForm] = useState(emptyBankAccountForm());
  const [banks, setBanks] = useState([]);
  const [banksError, setBanksError] = useState('');
  const [banksLoading, setBanksLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Read the value now, not inside the updater. React 16 pools synthetic
  // events: it clears `target` as soon as the handler returns, and a functional
  // updater runs later, during the render that applies it. Reaching for
  // `event.target.value` in there threw "Cannot read properties of null" and
  // unmounted the whole app, so picking a bank blanked the screen.
  const setField = (field) => (event) => {
    const { value } = event.target;
    setForm((current) => ({ ...current, [field]: value }));
  };

  // The catalog is per country, and nothing sequences these requests: switching
  // CL -> CO while the CL request is still in flight can land the CL banks in
  // state after the CO ones, leaving the operator submitting a bank id the CO
  // endpoint does not know. The cleanup flag drops any response whose country
  // is no longer the selected one.
  useEffect(() => {
    if (!country || !authToken) return undefined;

    let current = true;
    setBanksLoading(true);
    getBanks(country.toLowerCase(), authToken)
      .then((response) => {
        if (!current) return;
        setBanksError('');
        setBanks(parseBanks(response));
      })
      .catch((error) => {
        if (!current) return;
        setBanks([]);
        // Inline, not a modal: this fires on page load, and a dialog the operator
        // must dismiss before seeing the form reads as a crash. The reason still
        // has to be visible, so it replaces the generic line under the field.
        setBanksError(describe(error));
      })
      .finally(() => {
        if (current) setBanksLoading(false);
      });

    return () => {
      current = false;
    };
  }, [country, authToken]);

  // The bank catalog and the accepted account types are per country, so a
  // half-filled form carried across a country switch would submit values the
  // new country rejects.
  useEffect(() => {
    setForm(emptyBankAccountForm());
  }, [country]);

  const handleSubmit = async () => {
    const problems = validateBankAccountForm(country, form);
    if (problems.length) {
      swalErrorMessage(problems[0]);
      return;
    }

    const payload = buildBankAccountPayload(country, form);
    const bank = banks.find((option) => option.value === payload.bank_id);

    const confirmation = await Swal.fire({
      title: 'Confirmar cuenta bancaria',
      // Every interpolated value is escaped: the holder and the document are
      // free text and the bank label comes from the API, so unescaped markup
      // here would run in the admin origin.
      html: [
        'Vas a registrar el destino de pago del propietario',
        `<strong>${escapeHtml(form.personId.trim())}</strong>`,
        `para la propiedad <strong>${escapeHtml(payload.identifier)}</strong>:`,
        '<br/><br/>',
        `<strong>${escapeHtml(bank ? bank.label : payload.bank_id)}</strong><br/>`,
        `Cuenta ${escapeHtml(payload.account_number)}<br/>`,
        `Titular ${escapeHtml(payload.holder)} (${escapeHtml(payload.document)})`,
        '<br/><br/>',
        'Queda registrado en el sistema a tu nombre.',
      ].join(' '),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#5e72e4',
      cancelButtonColor: '#f5365c',
      confirmButtonText: 'Registrar cuenta',
      cancelButtonText: 'Cancelar',
    });
    if (!confirmation.isConfirmed) return;

    setIsSaving(true);
    try {
      const created = await createBankAccount(
        country.toLowerCase(),
        authToken,
        form.personId.trim(),
        payload,
      );
      await Swal.fire({
        title: 'Cuenta registrada',
        text: `Cuenta ${created.account_number} en ${created.bank_name} para la propiedad ${created.identifier}.`,
        icon: 'success',
      });
      setForm(emptyBankAccountForm());
    } catch (error) {
      swalErrorMessage(describe(error));
    } finally {
      setIsSaving(false);
    }
  };

  const accountTypes = ACCOUNT_TYPES[country] || [];
  const documentTypes = DOCUMENT_TYPES[country] || [];
  const documentLabel = DOCUMENT_LABEL[country] || 'Documento del titular';

  if (!accountTypes.length) {
    return (
      <div>
        <PageHeader
          title={'Ingresar nueva cuenta bancaria'}
          breadcrumb={[{ href: '#', title: 'Propietarios' }]}
        />
        <Container fluid>
          <Card>
            <CardBody>
              <Alert color="warning" className="mb-0">
                Selecciona un país en la barra superior para registrar una cuenta.
              </Alert>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={'Ingresar nueva cuenta bancaria'}
        breadcrumb={[{ href: '#', title: 'Propietarios' }]}
      />
      <Container fluid>
        <Card>
          <CardBody>
            <Alert color="warning">
              Esta cuenta pasa a ser el destino de los pagos del propietario para
              esa propiedad. Queda registrado quién la ingresó.
            </Alert>
            <Form onSubmit={(event) => event.preventDefault()}>
              <Row>
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">ID del propietario *</label>
                    <Input
                      type="text"
                      placeholder="Ej: 12345"
                      value={form.personId}
                      onChange={setField('personId')}
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">ID de la propiedad *</label>
                    <Input
                      type="text"
                      placeholder="Ej: 998877"
                      value={form.identifier}
                      onChange={setField('identifier')}
                    />
                    <small className="text-muted">
                      Es el identificador de la cuenta: una cuenta por propiedad.
                    </small>
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">Banco *</label>
                    <Input
                      type="select"
                      value={form.bankId}
                      onChange={setField('bankId')}
                      disabled={banksLoading || !banks.length}
                    >
                      <option value="">
                        {banksLoading ? 'Cargando bancos...' : '-- Selecciona --'}
                      </option>
                      {banks.map((bank) => (
                        <option key={bank.value} value={bank.value}>
                          {bank.label}
                        </option>
                      ))}
                    </Input>
                    {!banksLoading && !banks.length && (
                      <small className="text-danger">
                        {banksError || 'No se pudo cargar el listado de bancos.'}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">Tipo de cuenta *</label>
                    <Input
                      type="select"
                      value={form.accountType}
                      onChange={setField('accountType')}
                    >
                      <option value="">-- Selecciona --</option>
                      {accountTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">Número de cuenta *</label>
                    <Input
                      type="text"
                      placeholder="Solo dígitos"
                      value={form.accountNumber}
                      onChange={setField('accountNumber')}
                    />
                  </FormGroup>
                </Col>
                {hasClabe(country) && (
                  <Col md="3">
                    <FormGroup>
                      <label className="form-control-label">CLABE</label>
                      <Input
                        type="text"
                        placeholder="18 dígitos"
                        value={form.clabeNumber}
                        onChange={setField('clabeNumber')}
                      />
                      <small className="text-muted">
                        Opcional. Si se deja vacía, se genera automáticamente.
                      </small>
                    </FormGroup>
                  </Col>
                )}
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">Titular *</label>
                    <Input
                      type="text"
                      placeholder="Nombre completo"
                      value={form.holder}
                      onChange={setField('holder')}
                    />
                  </FormGroup>
                </Col>
                {hasDocumentType(country) && (
                  <Col md="3">
                    <FormGroup>
                      <label className="form-control-label">Tipo de documento *</label>
                      <Input
                        type="select"
                        value={form.documentType}
                        onChange={setField('documentType')}
                      >
                        <option value="">-- Selecciona --</option>
                        {documentTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                )}
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">{documentLabel} *</label>
                    <Input
                      type="text"
                      placeholder={DOCUMENT_PLACEHOLDER[country]}
                      value={form.document}
                      onChange={setField('document')}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">Email del titular *</label>
                    <Input
                      type="email"
                      placeholder="propietario@correo.com"
                      value={form.email}
                      onChange={setField('email')}
                    />
                  </FormGroup>
                </Col>
                {hasCity(country) && (
                  <Col md="3">
                    <FormGroup>
                      <label className="form-control-label">Ciudad *</label>
                      <Input
                        type="text"
                        placeholder="Ej: CDMX"
                        value={form.city}
                        onChange={setField('city')}
                      />
                    </FormGroup>
                  </Col>
                )}
                <Col md="3" className="d-flex align-items-center">
                  <FormGroup className="mb-0">
                    <Button color="primary" onClick={handleSubmit} disabled={isSaving}>
                      {isSaving ? 'Registrando...' : 'Registrar cuenta'}
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default NewAccount;
